import { toBlob } from "html-to-image";
import { erasureText, type PageElement } from "../state/types";
import { copyNativeText, isNative, shareNativeFile, shareNativeText } from "./platform";

/** Turns an erasure's title into a safe, readable filename stem. */
export const toFilename = (title: string | undefined, extension: string): string => {
  const stem =
    (title ?? "")
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .slice(0, 60) || "erasure";
  return `${stem}.${extension}`;
};

export const CONTENT_ELEMENT_ID = "content";

/**
 * Saves the rendered erasure as a PNG.
 *
 * The previous implementation called `window.saveAs`, which was never assigned,
 * so this silently did nothing; it now uses the imported `file-saver`.
 */
export const exportImage = async (title?: string): Promise<void> => {
  const node = document.getElementById(CONTENT_ELEMENT_ID);
  if (!node) throw new Error("Nothing to export yet.");

  // DOM rasterizers otherwise render transparent pixels for the page background,
  // so paint the element's own background colour in explicitly.
  const background = window.getComputedStyle(node).backgroundColor;
  const blob = await toBlob(node, { backgroundColor: background, pixelRatio: 2 });
  if (!blob) throw new Error("The rendered image was empty.");
  const filename = toFilename(title, "png");
  if (isNative()) {
    await shareNativeFile(blob, filename);
    return;
  }
  download(blob, filename);
};

const download = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};

/** Saves the surviving text — the poem itself — as a .txt file. */
export const exportText = async (page: PageElement[], title?: string): Promise<void> => {
  const text = erasureText(page);
  if (isNative()) {
    await shareNativeText(text, title ?? "Erasure");
    return;
  }
  download(
    new Blob([text], { type: "text/plain;charset=utf-8" }),
    toFilename(title, "txt"),
  );
};

/**
 * Copies the surviving text to the clipboard, resolving to `false` when the
 * browser denies clipboard access so the caller can fall back to a download.
 */
export const copyText = async (page: PageElement[]): Promise<boolean> => {
  const text = erasureText(page);
  try {
    if (isNative()) await copyNativeText(text);
    else await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};
